/**
 * SNS Links Panel Form
 *
 * Form layer: DOM element getters/setters only.
 */

import { Roles } from "../../constants/roles";
import { FormBase } from "../../util/form-base-form";

export class SNSLinksPanelForm extends FormBase {
    private readonly root: HTMLElement;

    public static getPanelRoots(root: ParentNode | Document = document): HTMLElement[] {
        return new SNSLinksPanelForm(document.documentElement).queryElements(
            root,
            `[data-role="${Roles.SNS_LINKS_PANEL}"][data-post-id]`,
            HTMLElement,
        );
    }

    private static matchesRole(element: Element | null, role: string): boolean {
        return !!element && element.getAttribute("data-role") === role;
    }

    /** Store the panel root element. */
    public constructor(root: HTMLElement) {
        super();
        this.root = root;
    }

    /** Return the panel root element. */
    public getRoot(): HTMLElement {
        return this.root;
    }

    /** Return the current post id. */
    public getPostId(): string {
        return this.root.getAttribute("data-post-id") || "create";
    }

    /** Return the editor element if present. */
    public getEditor(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${Roles.SNS_LINKS_EDITOR}"]`);
    }

    /** Return the open button if present. */
    public getOpenButton(): HTMLButtonElement | null {
        return this.getButton(this.root, `[data-role="${Roles.SNS_OPEN}"]`);
    }

    /** Return the close button if present. */
    public getCloseButton(): HTMLButtonElement | null {
        return this.getButton(this.root, `[data-role="${Roles.SNS_CLOSE}"]`);
    }

    /**
     * Backward compatibility if some pages still ship a Save button.
     */
    public getSaveButton(): HTMLButtonElement | null {
        return this.getButton(this.root, `[data-role="${Roles.SNS_SAVE}"]`);
    }

    /** Return the edit button if present. */
    public getEditButton(): HTMLButtonElement | null {
        return this.getButton(this.root, `[data-role="${Roles.SNS_EDIT}"]`);
    }

    /** Return the first SNS input inside the editor. */
    public getFirstInputInEditor(): HTMLInputElement | null {
        const editor = this.getEditor();
        if (!editor) {
            return null;
        }

        return this.getInput(editor, `[data-role="${Roles.SNS_LINK_INPUT}"]`);
    }

    /** Return the closest parent form. */
    public getClosestForm(): HTMLFormElement | null {
        return this.getForm(this.root);
    }

    /** Return the editable list container. */
    public getEditableList(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${Roles.SNS_LINKS_LIST}"]`);
    }

    /** Return all editable SNS input fields. */
    public getEditableListInputs(): HTMLInputElement[] {
        const list = this.getEditableList();
        if (!list) {
            return [];
        }

        return this.queryElements(list, `[data-role="${Roles.SNS_LINK_INPUT}"]`, HTMLInputElement);
    }

    /** Return all editable SNS item elements. */
    public getEditableListItems(): HTMLElement[] {
        const list = this.getEditableList();
        if (!list) {
            return [];
        }

        return this.queryElements(list, `[data-role="${Roles.SNS_LINK_ITEM}"]`, HTMLElement);
    }

    /** Resolve the clicked add tile from an event target. */
    public getAddTileFromEvent(event: Event): HTMLElement | null {
        const target = this.fromEventTarget(event);
        return this.closest(target, `[data-role="${Roles.SNS_ADD_TILE}"]`, HTMLElement);
    }

    /** Resolve the clicked remove button from an event target. */
    public getRemoveButtonFromEvent(event: Event): HTMLElement | null {
        const target = this.fromEventTarget(event);
        return this.closest(target, `[data-role="${Roles.SNS_TILE_REMOVE}"]`, HTMLElement);
    }

    /** Return the SNS input rendered inside an item. */
    public getItemInput(item: HTMLElement): HTMLInputElement | null {
        return this.getInput(item, `[data-role="${Roles.SNS_LINK_INPUT}"]`);
    }
}
